import type { CorridorRouteGuide } from "@/types/corridor";

/**
 * Route-specific guides for AI Overviews and SEO — unique per corridor, not templates.
 * Merged into corridor definitions via registry.ts.
 */
export const CORRIDOR_ROUTE_GUIDES: Record<string, CorridorRouteGuide> = {
  "hyderabad-vijayawada": {
    overview:
      "The Hyderabad–Vijayawada corridor runs 275 km along NH65 through Suryapet and Kodad — one of the busiest Telangana–Andhra Pradesh links. IT professionals from Hitech City and Gachibowli head home for weekends; Vijayawada students and families ride up for Numaish, medical visits, and temple trips at Kanaka Durga. Duri hosts already drive this stretch daily, so seats open up in predictable morning and evening windows instead of last-minute cab surges.",
    typicalBreak: "Suryapet or Kodad (15 min)",
    tollsNote: "3 plazas, ~₹350 one-way",
    useCases: [
      {
        title: "Weekend home travel",
        audience: "IT & corporate professionals",
        description:
          "Friday evening and Saturday morning are peak for Hitech City → Benz Circle. Book a verified seat by Thursday night — most hosts leave between 5:30–7:30 PM to beat toll queues at Outer Ring Road.",
        tip: "Round-trip bookings (Fri out, Sun back) fill fastest on this route.",
      },
      {
        title: "Office commute (split week)",
        audience: "Hybrid workers between both cities",
        description:
          "Some consultants and project teams split the week between Hyderabad offices and Vijayawada client sites. Mid-week carpools at 6 AM from LB Nagar or Uppal cut 4+ hours of solo driving and recover toll costs.",
      },
      {
        title: "Festival & family travel",
        audience: "Families during Sankranti, Dasara, Ugadi",
        description:
          "Bus queues at MGBS and Vijayawada RTC stretch for hours during festivals. Duri carpools with women-preferred filters and luggage-friendly sedans are popular when entire families travel together.",
        tip: "Book 5–7 days ahead for Sankranti week — availability doubles but seats go fast.",
      },
      {
        title: "College & exam travel",
        audience: "Students at BITS, OU, VIT-AP corridor",
        description:
          "Students heading to Vijayawada for exams or semester breaks use Duri from Gachibowli and Miyapur. Women-only rides are commonly available on Friday afternoons.",
      },
    ],
    availabilityWindows: [
      {
        label: "Hyderabad → Vijayawada (weekdays)",
        times: "5:30–7:30 AM · 5:00–8:00 PM",
        note: "Morning slots suit same-day meetings in Vijayawada; evening runs target weekend starters.",
      },
      {
        label: "Vijayawada → Hyderabad (weekdays)",
        times: "6:00–8:00 AM · 4:30–7:30 PM",
        note: "Return traffic peaks Sunday 6–9 PM when students and professionals head back for Monday.",
      },
      {
        label: "Weekends & public holidays",
        times: "Every 2–3 hours, 6 AM–9 PM",
        note: "Highest supply Fri–Sun. Sankranti and Dasara add extra hosts recovering festival travel costs.",
      },
    ],
    trustSteps: [
      {
        step: "Verify before you book",
        detail:
          "Every host and guest on this route completes Aadhaar verification. Vehicle RC, seat count, and past ratings are visible before payment.",
      },
      {
        step: "Share live trip status",
        detail:
          "On NH65 stretches through Suryapet, family members can follow the ride in-app. SOS reaches Duri support within seconds.",
      },
      {
        step: "Choose comfort filters",
        detail:
          "Women-only and women-preferred rides are widely listed on HYD–VJA. AC sedans and SUVs only — no shared autos or unverified vehicles.",
      },
      {
        step: "Rate every trip",
        detail:
          "Post-ride ratings keep quality high on this busy corridor. Hosts with 4.5+ stars and 20+ completed HYD–VJA trips are labelled experienced on the route.",
      },
    ],
    whyDuri: {
      title: "Why Duri on Hyderabad–Vijayawada specifically",
      points: [
        "2,980+ travellers have already carpooled this corridor — real supply, not empty listings.",
        "Typical seat fare ₹499 vs ₹750 AC bus or ₹4,500 private cab — savings hold even with NH65 tolls split four ways.",
        "Pickup zones match how people actually travel: Hitech City, Gachibowli, LB Nagar on the Hyderabad side; Benz Circle, PVP Mall, Kanaka Durga on the Vijayawada side.",
        "Both directions every day — not a one-way charter. Return rides Sunday evening are as easy to find as Friday outbound.",
        "Hosts who drive NH65 regularly list recurring trips, so repeat commuters can book the same verified driver each week.",
      ],
    },
  },

  "hyderabad-guntur": {
    overview:
      "Hyderabad to Guntur is a 285 km run on NH65 via Vijayawada belt — slightly longer than HYD–VJA but with the same festival and family-travel demand. Guntur families in Nallapadu and Brodipet visit Hyderabad for healthcare and shopping; Hyderabad hosts drive down for Sankranti and crop-season visits. Seat fares typically start around ₹479 — well under AC bus and outstation cab rates.",
    typicalBreak: "Suryapet or Chilakaluripet (15–20 min)",
    tollsNote: "4 plazas, ~₹400 one-way",
    useCases: [
      {
        title: "Family weekend visits",
        audience: "Guntur families with Hyderabad relatives",
        description:
          "Saturday morning departures from Gachibowli and LB Nagar are popular when entire families travel with luggage. Sedans with 3 seats booked beat cramped RTC buses on this 5–6 hour stretch.",
      },
      {
        title: "Amaravati-region work travel",
        audience: "Consultants & government project staff",
        description:
          "Professionals on Amaravati and capital-region assignments often split the week between Hyderabad HQ and Guntur sites. Tuesday/Wednesday carpools recover fuel on routes hosts already drive.",
      },
      {
        title: "Student & campus travel",
        audience: "RVR, JNTUK, medical college students",
        description:
          "Semester breaks and exam windows bring steady demand from Guntur colleges to Hyderabad. Friday 4–7 PM slots from Nallapadu and Arundelpet fill quickly.",
        tip: "Book women-preferred rides for late-evening Friday departures.",
      },
      {
        title: "Medical & shopping trips",
        audience: "Patients heading to Hyderabad hospitals",
        description:
          "KIMS, Apollo, and NIMS in Hyderabad draw Guntur patients weekly. Carpool beats ambulance-adjacent cab pricing for stable patients who can sit in a sedan.",
      },
    ],
    availabilityWindows: [
      {
        label: "Hyderabad → Guntur",
        times: "6:00–8:00 AM · 5:00–7:30 PM",
        note: "Evening Friday runs target weekend arrivals before dinner in Guntur.",
      },
      {
        label: "Guntur → Hyderabad",
        times: "6:30–8:30 AM · 4:00–7:00 PM",
        note: "Sunday 5–8 PM peak for return to Monday offices in Hitech City.",
      },
      {
        label: "Festival weeks",
        times: "6 AM–9 PM, every 2–3 hours",
        note: "Sankranti and Ugadi see doubled host supply — book 4–5 days early.",
      },
    ],
    trustSteps: [
      {
        step: "ID-verified hosts only",
        detail: "Aadhaar verification is mandatory. RC and insurance details show on every Guntur-route listing.",
      },
      {
        step: "Highway stop transparency",
        detail: "Hosts typically break at Suryapet — agreed in chat before booking so families can plan meals.",
      },
      {
        step: "Women & family filters",
        detail: "Women-only and family-friendly filters are common on this route during festival travel.",
      },
      {
        step: "Ratings per corridor",
        detail: "See how many completed Hyderabad–Guntur trips a host has — not just overall stars.",
      },
    ],
    whyDuri: {
      title: "Why Duri on Hyderabad–Guntur specifically",
      points: [
        "2,140+ travellers on this corridor — established host supply on NH65.",
        "₹479/seat typical vs ₹700 bus or ₹4,200 cab — tolls at Kaza split across four seats.",
        "Pickup zones: Hitech City, Gachibowli, LB Nagar ↔ Nallapadu, Brodipet, Arundelpet.",
        "Return rides listed same day — Guntur → Hyderabad Sunday evening is reliable.",
        "Hosts recovering festival travel costs list extra Sankranti week trips.",
      ],
    },
  },

  "hyderabad-khammam": {
    overview:
      "Hyderabad to Khammam is a shorter 195 km NH65 hop (3–4 hours) — popular with Telangana families visiting Khammam Fort area, Wyra Road relatives, and coal-belt workers who split time between both cities. It's a natural day-trip or same-day-return corridor with lower per-seat cost than Vijayawada-length routes.",
    typicalBreak: "Suryapet (10 min)",
    tollsNote: "2 plazas, ~₹220 one-way",
    useCases: [
      {
        title: "Same-day family visits",
        audience: "Weekend day-trippers",
        description:
          "Leave Hyderabad 6 AM, reach Khammam by 9:30, return same evening — carpools make this affordable vs ₹3,200 one-way cab.",
      },
      {
        title: "Coal & industrial belt commute",
        audience: "Shift workers & site supervisors",
        description:
          "Weekly rotations between Hyderabad base and Khammam sites suit recurring carpools with the same host each Monday.",
      },
      {
        title: "Student home travel",
        audience: "Khammam district students in Hyderabad colleges",
        description:
          "Monthly home trips from LB Nagar and Uppal pickups — women-only options on Saturday mornings.",
      },
      {
        title: "Festival family runs",
        audience: "Bathukamma & Dasara travellers",
        description:
          "Short distance means more hosts willing to drive both ways in one weekend — book round-trip early.",
      },
    ],
    availabilityWindows: [
      {
        label: "Hyderabad → Khammam (daily)",
        times: "6:00–8:00 AM · 4:00–6:30 PM",
        note: "Morning slots popular for same-day return plans.",
      },
      {
        label: "Khammam → Hyderabad",
        times: "7:00–9:00 AM · 5:00–8:00 PM",
        note: "Sunday evening return before Hyderabad work week.",
      },
      {
        label: "Weekends",
        times: "Every 2–3 hours, 6 AM–8 PM",
        note: "Higher frequency than longer corridors — seats open same day often.",
      },
    ],
    trustSteps: [
      {
        step: "Verified profiles required",
        detail: "Full Aadhaar check before first booking on any Khammam-route seat.",
      },
      {
        step: "Live tracking on NH65",
        detail: "Share trip link with family for the Suryapet–Khammam stretch.",
      },
      {
        step: "Clear seat pricing",
        detail: "₹329 typical — shown upfront, no toll negotiation at pickup.",
      },
      {
        step: "Post-trip ratings",
        detail: "Rate hosts after every trip to keep the short-corridor network trustworthy.",
      },
    ],
    whyDuri: {
      title: "Why Duri on Hyderabad–Khammam specifically",
      points: [
        "620+ completed rides — reliable same-day supply.",
        "₹329/seat vs ₹420 bus or ₹3,200 cab on 195 km.",
        "Zones: Hitech City, Gachibowli, LB Nagar ↔ Wyra Road, Khammam Fort, NSP Colony.",
        "Short drive time suits repeat weekly carpools with one verified host.",
        "Both directions listed — not a one-way charter market.",
      ],
    },
  },

  "hyderabad-bangalore": {
    overview:
      "Hyderabad to Bangalore is India's busiest tech-highway at 570 km on NH44 (9–11 hours). IT professionals transfer between campuses, interview candidates fly this route weekly, and weekend home travellers avoid ₹12,000 outstation cabs by sharing sedans overnight. Duri pools typically start around ₹1,299 per seat — a fraction of solo cab cost.",
    typicalBreak: "Kurnool or Anantapur (20 min)",
    tollsNote: "8+ plazas, ~₹900 one-way",
    useCases: [
      {
        title: "Tech campus transfers",
        audience: "IT professionals between HYD & BLR offices",
        description:
          "Project moves between Hitech City and Whitefield/Electronic City drive steady weekday demand. Overnight departures (9–10 PM) arrive for 7 AM stand-ups.",
        tip: "Book overnight pools 2–3 days ahead — seats fill for Monday arrivals.",
      },
      {
        title: "Interview & onboarding travel",
        audience: "Candidates & new joiners",
        description:
          "One-way pools from Gachibowli to Manyata or Koramangala are common when people relocate between Telangana and Karnataka jobs.",
      },
      {
        title: "Weekend home travel",
        audience: "Hyderabad natives working in Bengaluru",
        description:
          "Friday night outbound and Sunday night return — the classic NH44 pattern. Luggage-friendly SUVs listed during long weekends.",
      },
      {
        title: "Host fuel recovery",
        audience: "Drivers who make this trip anyway",
        description:
          "Hosts driving Hyderabad–Bengaluru for family already list 2–3 seats to recover ₹2,000+ in fuel and tolls each way.",
      },
    ],
    availabilityWindows: [
      {
        label: "Hyderabad → Bangalore",
        times: "9:00–11:00 PM (overnight) · 5:00–7:00 AM",
        note: "Overnight is the norm — avoids daytime heat and traffic at Kurnool.",
      },
      {
        label: "Bangalore → Hyderabad",
        times: "9:00 PM–12:00 AM · 6:00–8:00 AM",
        note: "Sunday 9 PM–midnight peak for Monday Hyderabad office.",
      },
      {
        label: "Long weekends",
        times: "Thu–Sun, 2–3 pools per day",
        note: "Republic Day, Independence Day, and year-end see extra host supply.",
      },
    ],
    trustSteps: [
      {
        step: "Long-haul verification",
        detail: "Hosts on 570 km routes must show valid RC, insurance, and 10+ completed intercity trips or high ratings.",
      },
      {
        step: "Overnight safety",
        detail: "Women-only overnight pools available. Share live location with family for the full NH44 stretch.",
      },
      {
        step: "Break stops agreed in chat",
        detail: "Kurnool or Anantapur meal breaks — confirm before booking for comfort on 9+ hour drives.",
      },
      {
        step: "SOS on highway",
        detail: "In-app SOS and support line active throughout overnight legs.",
      },
    ],
    whyDuri: {
      title: "Why Duri on Hyderabad–Bangalore specifically",
      points: [
        "890+ completed rides on India's top tech corridor.",
        "₹1,299/seat vs ₹1,200 bus (sleeper) or ₹12,000 cab — faster than bus, cheaper than cab.",
        "Pickup: Hitech City, Gachibowli, Kondapur ↔ Whitefield, Electronic City, Koramangala.",
        "Overnight pools listed both ways every week — not seasonal.",
        "Same verified hosts on recurring corporate shuttles — book the driver you trust.",
      ],
    },
  },

  "vijayawada-visakhapatnam": {
    overview:
      "Vijayawada to Visakhapatnam covers 350 km on NH16 along the Andhra coast (6–7.5 hours). Port-city professionals, beach-weekend families, and Vizag students connect through Benz Circle and RK Beach pickups. Coastal scenery and steady RTC competition make ₹549 carpool seats attractive vs ₹650 bus or ₹6,500 cab.",
    typicalBreak: "Rajahmundry or Tuni (15 min)",
    tollsNote: "5 plazas, ~₹450 one-way",
    useCases: [
      {
        title: "Beach & weekend getaways",
        audience: "Vijayawada families",
        description:
          "Friday evening Benz Circle → MVP Colony runs are popular for RK Beach weekends. Return Sunday 4–7 PM.",
      },
      {
        title: "Port & industrial travel",
        audience: "Vizag steel & port sector staff",
        description:
          "Weekly commutes between Vijayawada HQ and Gajuwaka plants suit recurring carpools.",
      },
      {
        title: "College & campus moves",
        audience: "Andhra University & GITAM students",
        description:
          "Semester start/end brings high demand from Gunadala and Auto Nagar to Rushikonda.",
        tip: "Women-preferred rides common on Friday coastal departures.",
      },
      {
        title: "Medical referrals",
        audience: "Patients to Vizag specialty hospitals",
        description:
          "Families prefer verified sedans over crowded buses for post-surgery travel on NH16.",
      },
    ],
    availabilityWindows: [
      {
        label: "Vijayawada → Visakhapatnam",
        times: "6:00–8:00 AM · 5:00–7:30 PM",
        note: "Friday evening peak for weekend beach travel.",
      },
      {
        label: "Visakhapatnam → Vijayawada",
        times: "7:00–9:00 AM · 4:30–7:30 PM",
        note: "Sunday return before Monday work in Vijayawada.",
      },
      {
        label: "Holiday weekends",
        times: "Every 3 hours, 6 AM–9 PM",
        note: "Dasara and summer holidays add coastal tourism demand.",
      },
    ],
    trustSteps: [
      {
        step: "Coastal route verification",
        detail: "All hosts Aadhaar-verified with vehicle photos visible before payment.",
      },
      {
        step: "NH16 tracking",
        detail: "Live trip share for long coastal stretches through Rajahmundry belt.",
      },
      {
        step: "Women & family options",
        detail: "Filters for women-only and family-friendly listings on weekend runs.",
      },
      {
        step: "Corridor-specific ratings",
        detail: "Review counts for Vijayawada–Vizag trips shown on host profile.",
      },
    ],
    whyDuri: {
      title: "Why Duri on Vijayawada–Visakhapatnam specifically",
      points: [
        "740+ travellers on the coastal NH16 corridor.",
        "₹549/seat vs ₹650 AC bus or ₹6,500 cab.",
        "Benz Circle, PVP, Gunadala ↔ RK Beach, MVP Colony, Gajuwaka.",
        "Both directions daily — weekend beach traffic is two-way.",
        "Hosts list Friday outbound and Sunday return as paired trips.",
      ],
    },
  },

  "vijayawada-guntur": {
    overview:
      "Vijayawada to Guntur is only 35 km on NH16 (45–70 minutes) — one of Duri's highest-frequency micro-corridors with 2,100+ completed rides. College students, hospital staff, and Amaravati-region office workers make this hop daily. At ₹99/seat it undercuts ₹80–100 bus hassle and ₹900 cab fares for a trip that cabs often overcharge.",
    typicalBreak: "Usually non-stop or quick tea near Mangalagiri",
    tollsNote: "Minimal tolls, ~₹50 one-way",
    useCases: [
      {
        title: "Daily college commute",
        audience: "RVR, JNTUK, VIT-AP students",
        description:
          "Weekday 7:30–8:30 AM from Benz Circle to Nallapadu and Brodipet — same host, same seat, every week.",
      },
      {
        title: "Hospital & clinic staff",
        audience: "Medical workers between cities",
        description:
          "Shift workers at Vijayawada and Guntur hospitals carpool for 6 AM and 2 PM changeovers.",
      },
      {
        title: "Business & market travel",
        audience: "Traders & wholesale buyers",
        description:
          "Morning runs to Guntur markets and evening returns with goods — sedans with boot space listed.",
      },
      {
        title: "Family errands",
        audience: "Suburban families",
        description:
          "Short hop makes same-day multiple trips viable — Enikepadu to Arundelpet and back.",
        tip: "Book recurring weekday slots — hosts prefer regular guests on this route.",
      },
    ],
    availabilityWindows: [
      {
        label: "Weekday mornings",
        times: "7:00–9:30 AM (both directions)",
        note: "Highest frequency — seats every 30–60 minutes on peak days.",
      },
      {
        label: "Weekday evenings",
        times: "5:00–7:30 PM",
        note: "Return from Guntur colleges and offices to Vijayawada homes.",
      },
      {
        label: "Weekends",
        times: "8 AM–8 PM, hourly pools",
        note: "Shopping and family visit demand — still short-hop pricing.",
      },
    ],
    trustSteps: [
      {
        step: "Quick-trip verification",
        detail: "Even on 45-minute hops, full Aadhaar verification applies.",
      },
      {
        step: "Known-host preference",
        detail: "Repeat commuters save favourite hosts — ratings matter more on daily routes.",
      },
      {
        step: "Women morning pools",
        detail: "Women-only 7:30 AM slots widely available Benz Circle ↔ Guntur.",
      },
      {
        step: "Transparent ₹99 pricing",
        detail: "Per-seat fare fixed in app — no auto-negotiation at pickup.",
      },
    ],
    whyDuri: {
      title: "Why Duri on Vijayawada–Guntur specifically",
      points: [
        "2,100+ completed rides — Duri's densest micro-corridor.",
        "₹99/seat vs ₹900 cab for a 35 km hop.",
        "Benz Circle, Auto Nagar, Enikepadu ↔ Brodipet, Nallapadu, Arundelpet.",
        "22+ rides listed many weekdays — real-time supply.",
        "Ideal for recurring daily commute — same verified driver each week.",
      ],
    },
  },

  "vijayawada-nellore": {
    overview:
      "Vijayawada to Nellore is 280 km on NH16 through the rice-belt plains (5–6 hours). Families visit relatives in Nellore district, students connect to Nellore colleges, and coastal-bound travellers use this as a leg toward Chennai. Carpool seats from ₹449 beat ₹550 bus and ₹5,200 cab on long straight highway stretches.",
    typicalBreak: "Ongole or Kavali (15 min)",
    tollsNote: "4 plazas, ~₹380 one-way",
    useCases: [
      {
        title: "Festival family travel",
        audience: "Sankranti & Ugadi visitors",
        description:
          "Nellore district family visits from Vijayawada spike on festival eves — book 3–4 days ahead.",
      },
      {
        title: "Agricultural & market trips",
        audience: "Traders & commission agents",
        description:
          "Weekly market runs from Benz Circle to Nellore Bus Stand and Magunta — early 5 AM departures.",
      },
      {
        title: "Medical travel",
        audience: "Referral patients",
        description:
          "Nellore and Chennai-bound patients often stage through Vijayawada — verified sedans preferred.",
      },
      {
        title: "Multi-leg coastal travel",
        audience: "Travellers continuing to Chennai",
        description:
          "Some guests book Vijayawada–Nellore then Nellore–Chennai next day — Duri hosts cover both legs.",
        tip: "Search return Nellore–Vijayawada when booking round trips.",
      },
    ],
    availabilityWindows: [
      {
        label: "Vijayawada → Nellore",
        times: "5:30–7:30 AM · 4:00–6:30 PM",
        note: "Morning slots for same-day Nellore arrivals.",
      },
      {
        label: "Nellore → Vijayawada",
        times: "6:00–8:00 AM · 5:00–7:30 PM",
        note: "Sunday evening return pattern.",
      },
      {
        label: "Festival weeks",
        times: "6 AM–8 PM, every 2–3 hours",
        note: "Sankranti sees strongest Nellore-district inbound traffic.",
      },
    ],
    trustSteps: [
      {
        step: "Full ID verification",
        detail: "Aadhaar-verified hosts and guests on all NH16 long-haul listings.",
      },
      {
        step: "Highway SOS",
        detail: "Live tracking and SOS on 5+ hour plains highway stretches.",
      },
      {
        step: "Women & family filters",
        detail: "Available on festival and family-travel bookings.",
      },
      {
        step: "Route experience shown",
        detail: "Host profiles display completed Vijayawada–Nellore trip count.",
      },
    ],
    whyDuri: {
      title: "Why Duri on Vijayawada–Nellore specifically",
      points: [
        "510+ completed rides on NH16 rice-belt corridor.",
        "₹449/seat vs ₹550 bus or ₹5,200 cab.",
        "Benz Circle, Kanuru, Poranki ↔ Nellore Bus Stand, Magunta, Vedayapalem.",
        "Connects to Nellore–Chennai leg for coastal through-travel.",
        "Both directions listed — return rides on Sunday reliable.",
      ],
    },
  },

  "nellore-chennai": {
    overview:
      "Nellore to Chennai is 180 km on NH16 (3.5–4.5 hours) into the Tamil Nadu capital — a daily artery for students, IT commuters staging from Nellore, and patients heading to Chennai hospitals. OMR and Tambaram drops are standard. ₹349 carpool seats undercut ₹380 bus and ₹4,200 cab on this high-volume coastal approach.",
    typicalBreak: "Gudur or Tada (10–15 min)",
    tollsNote: "3 plazas, ~₹280 one-way",
    useCases: [
      {
        title: "Student commute to Chennai",
        audience: "Nellore district students",
        description:
          "Daily and weekly pools from Nellore Bus Stand to OMR colleges — morning 6–7 AM departures.",
      },
      {
        title: "IT & office staging",
        audience: "Nellore residents working in Chennai",
        description:
          "Weekly Chennai work with Nellore home — Monday AM out, Friday PM back is the common pattern.",
      },
      {
        title: "Hospital & treatment travel",
        audience: "Patients to Chennai specialty care",
        description:
          "Apollo, MIOT, and government hospital referrals from Nellore district use verified carpools.",
      },
      {
        title: "Airport & travel connections",
        audience: "Flyers via Chennai airport",
        description:
          "Early-morning Nellore → Tambaram/OMR pools connect to Chennai airport departures.",
        tip: "Book 4 AM pools for 8 AM flights — hosts list these mid-week.",
      },
    ],
    availabilityWindows: [
      {
        label: "Nellore → Chennai (weekdays)",
        times: "5:30–7:30 AM · 4:00–6:00 PM",
        note: "Morning rush toward OMR and Tambaram tech belt.",
      },
      {
        label: "Chennai → Nellore",
        times: "6:00–8:00 AM · 5:00–8:00 PM",
        note: "Friday evening and Sunday return to Nellore homes.",
      },
      {
        label: "Weekends",
        times: "Every 2–3 hours, 6 AM–8 PM",
        note: "Family visit and shopping trips into Chennai.",
      },
    ],
    trustSteps: [
      {
        step: "Cross-state verification",
        detail: "AP and TN travellers both Aadhaar-verified — profiles visible before booking.",
      },
      {
        step: "NH16 live tracking",
        detail: "Share trip with family on Tada–Chennai approach.",
      },
      {
        step: "Women-preferred pools",
        detail: "Common on student and office morning commutes.",
      },
      {
        step: "Upfront ₹349 pricing",
        detail: "No surge pricing at Tada toll — fare locked in app.",
      },
    ],
    whyDuri: {
      title: "Why Duri on Nellore–Chennai specifically",
      points: [
        "960+ completed rides on NH16 into Chennai.",
        "₹349/seat vs ₹380 bus or ₹4,200 cab.",
        "Nellore Bus Stand, Magunta ↔ OMR, Tambaram, Velachery.",
        "Links from Vijayawada–Nellore for multi-leg coastal travel.",
        "Daily weekday supply — not just weekend charter.",
      ],
    },
  },

  "nellore-tirupati": {
    overview:
      "Nellore to Tirupati is 135 km via NH16 and NH140 (2.5–3.5 hours) — a pilgrimage and family corridor with spikes around Brahmotsavam, Vaikunta Ekadasi, and weekends. Alipiri and Tiruchanoor drops are standard. ₹229 carpool seats beat ₹280 bus queues and ₹2,800 festival-surged cab fares.",
    typicalBreak: "Gudur or Naidupet (10 min)",
    tollsNote: "2 plazas, ~₹180 one-way",
    useCases: [
      {
        title: "Weekend darshan",
        audience: "Families from Nellore district",
        description:
          "Saturday morning Nellore → Alipiri, Sunday evening return — the classic pilgrimage pattern.",
        tip: "Book 7–10 days ahead during Brahmotsavam — cab fares triple; carpools hold pricing.",
      },
      {
        title: "Festival season travel",
        audience: "Pilgrims during peak temple events",
        description:
          "Sedans with 3–4 seats reduce per-person cost when families travel together vs multiple autos.",
      },
      {
        title: "Naidupeta & Sullurpeta feeders",
        audience: "Suburban Nellore travellers",
        description:
          "Pickup from Sullurpeta and Gudur on the way to Tirupati — hosts list these as route stops.",
      },
      {
        title: "Medical & family visits",
        audience: "Non-pilgrimage travel to Tirupati",
        description:
          "Tirupati hospitals and colleges draw Nellore visitors year-round beyond festival peaks.",
      },
    ],
    availabilityWindows: [
      {
        label: "Nellore → Tirupati",
        times: "5:00–7:30 AM · 3:00–5:30 PM",
        note: "Early morning for temple darshan queues.",
      },
      {
        label: "Tirupati → Nellore",
        times: "12:00–3:00 PM · 6:00–8:30 PM",
        note: "Post-darshan afternoon returns common.",
      },
      {
        label: "Festival peaks",
        times: "4 AM–9 PM, continuous pools",
        note: "Brahmotsavam and summer holidays — book early.",
      },
    ],
    trustSteps: [
      {
        step: "Pilgrimage-route verification",
        detail: "Verified hosts only — critical when families travel with elders.",
      },
      {
        step: "Women & family filters",
        detail: "Widely used on temple weekend travel.",
      },
      {
        step: "Luggage clarity in chat",
        detail: "Confirm prasadam and bag space before booking.",
      },
      {
        step: "Festival SOS support",
        detail: "Extra support staffing during Brahmotsavam peak weeks.",
      },
    ],
    whyDuri: {
      title: "Why Duri on Nellore–Tirupati specifically",
      points: [
        "1,200+ completed pilgrimage-corridor rides.",
        "₹229/seat vs ₹280 bus or ₹2,800 festival cab.",
        "Nellore Bus Stand, Sullurpeta ↔ Alipiri, Tiruchanoor, Renigunta.",
        "Hosts list festival-week extra trips — predictable pricing.",
        "Connects to Chennai–Tirupati for multi-origin pilgrims.",
      ],
    },
  },

  "chennai-tirupati": {
    overview:
      "Chennai to Tirupati is 135 km on NH48/NH140 (3–4 hours) — one of South India's busiest devotional routes. Tambaram and Chromepet feeders join OMR pools toward Alipiri. Weekend and festival demand is extreme; ₹279 carpool seats vs ₹3,500 cabs during peak darshan weeks.",
    typicalBreak: "Chittoor or Renigunta (10–15 min)",
    tollsNote: "2 plazas, ~₹200 one-way",
    useCases: [
      {
        title: "Weekend temple trips",
        audience: "Chennai families",
        description:
          "Friday night or Saturday 4 AM departures from Tambaram — back Sunday evening for Monday work.",
        tip: "Saturday 4 AM pools fill by Thursday — set alerts in the app.",
      },
      {
        title: "Festival & holiday peaks",
        audience: "Brahmotsavam & long-weekend pilgrims",
        description:
          "When Chennai cab fares spike 3×, carpools keep per-seat pricing near ₹279–₹350.",
      },
      {
        title: "Senior citizen family travel",
        audience: "Families with elderly pilgrims",
        description:
          "Sedans with fewer passengers and AC comfort — women-preferred filters common.",
      },
      {
        title: "Renigunta & airport connections",
        audience: "Flyers staging via Tirupati",
        description:
          "Some hosts drop at Renigunta for airport connections — confirm in chat.",
      },
    ],
    availabilityWindows: [
      {
        label: "Chennai → Tirupati (weekends)",
        times: "4:00–7:00 AM · 9:00 PM–12:00 AM Fri",
        note: "Pre-dawn Saturday arrivals for temple queue.",
      },
      {
        label: "Tirupati → Chennai",
        times: "1:00–4:00 PM · 7:00–10:00 PM Sun",
        note: "Post-darshan Sunday return before work week.",
      },
      {
        label: "Festival season",
        times: "3 AM–10 PM daily",
        note: "Brahmotsavam — highest annual demand on this corridor.",
      },
    ],
    trustSteps: [
      {
        step: "Verified pilgrimage hosts",
        detail: "Experienced Tirupati-route hosts show trip count and ratings.",
      },
      {
        step: "Women-only weekend pools",
        detail: "Listed frequently on Saturday pre-dawn departures.",
      },
      {
        step: "Live tracking NH48",
        detail: "Share location through Tada and Renigunta stretches.",
      },
      {
        step: "No festival surge in-app",
        detail: "Seat price set at listing — not metered like cabs at Alipiri.",
      },
    ],
    whyDuri: {
      title: "Why Duri on Chennai–Tirupati specifically",
      points: [
        "1,550+ completed rides — Duri's top pilgrimage corridor.",
        "₹279/seat vs ₹320 bus or ₹3,500 peak cab.",
        "Tambaram, Chromepet, OMR ↔ Alipiri, Tiruchanoor, Renigunta.",
        "Friday–Sunday cycle every week — reliable two-way supply.",
        "Festival weeks add hosts recovering trip costs — book early.",
      ],
    },
  },
};
